import { FC, ReactNode, useEffect, useState } from "react";
import { KeycloakConfig } from "keycloak-js";
import { SplashScreen } from "./SplashScreen";
import { Card } from "../Card";
import { Paragraph } from "../Paragraph";
import { ContentContainer } from "../content/ContentContainer";

export interface AppMetadata {
  keycloakConfig: KeycloakConfig
  colorPalette: string | { [tint: string]: string }
  brandTitle: string
  brandSubTitle: string | null
  brandImageURL: string | null
}
export interface AppMetadataResponse {
  keycloak_config: {
    url: string
    realm: string
    client_id: string
  }
  color_palette: string | { [tint: string]: string }
  brand_title: string
  brand_sub_title: string | null
  brand_image_url: string | null
}
export const AppMetadataResolver: FC<{ builder: (metadata: AppMetadata) => ReactNode, metadataUrl: string }> = props => {
  const [metadata, setMetadata] = useState<AppMetadata | null>(loadCachedMetaData())
  const [error, setError] = useState<string>()
  useEffect(() => {
    if (metadata === null) {
      getAppMetadata(props.metadataUrl).then(metadata => {
        const parsedMetadata = parseMetadata(metadata)
        storeCachedMetadata(parsedMetadata)
        setMetadata(parsedMetadata)
      }).catch(() => setError('Something has gone terribly wrong'))
    } else {
      window.document.title = metadata.brandTitle
    }
  }, [metadata])
  if (metadata === null) {
    if (error) {
      return <SplashScreen>
        <ContentContainer size={"sm"}>
          <Card title={'Error'}>
            <Paragraph>{error}</Paragraph>
            <button onClick={() => window.location.reload()} className={"underline"}>Retry</button>
          </Card>
        </ContentContainer>
      </SplashScreen>
    }
    return <SplashScreen />
  }
  return props.builder(metadata)
}

async function getAppMetadata(url: string): Promise<AppMetadataResponse> {
  const response = await fetch(url)
  const json = await response.json()
  return json.item as AppMetadataResponse
}

function parseMetadata(metadata: AppMetadataResponse): AppMetadata {
  return {
    keycloakConfig: {
      url: metadata.keycloak_config.url,
      realm: metadata.keycloak_config.realm,
      clientId: metadata.keycloak_config.client_id
    },
    colorPalette: metadata.color_palette,
    brandTitle: metadata.brand_title,
    brandSubTitle: metadata.brand_sub_title,
    brandImageURL: metadata.brand_image_url
  }
}

function loadCachedMetaData(): AppMetadata | null {
  const cached = window.localStorage.getItem('app_metadata')
  if (cached === null) {
    return null
  }
  try {
    const t = new Date().getTime()
    const metadata = JSON.parse(cached) as unknown as { expiry: number, value: AppMetadata }
    if (metadata.expiry > t) {
      console.log('using cached metadata')
      return metadata.value
    }
    console.log('cached metadata expired', t, metadata)
    return null
  } catch {
    console.warn('error while parsing cached metadata')
    return null
  }
}
function storeCachedMetadata(metadata: AppMetadata): void {
  console.log('storing cached metadata')
  window.localStorage.setItem('app_metadata', JSON.stringify({
    expiry: new Date().getTime() + (1000 * 30),
    value: metadata
  }))
}
